import { Invoice, SharedProps } from '@/types'
import { router, usePage } from '@inertiajs/react'
import axios from 'axios'
import { useEffect } from 'react'

const POLL_INTERVAL = 2500
const MAX_FOLLOW_DURATION = 3 * 60 * 1000

// Steps the platform takes on its own within seconds of the upload; past them the invoice waits for the client.
const IN_FLIGHT_STATUSES = [null, 'api:uploaded', 'api:validated', 'api:sent', 'fr:200', 'fr:201', 'fr:202']

const NEXT_STEP_LABELS: Record<string, string> = {
  none: 'Envoi à la plateforme',
  'api:uploaded': 'Contrôles de la plateforme',
  'api:validated': 'Transmission à la plateforme du client',
  'api:sent': 'Transmission à la plateforme du client',
  'fr:200': 'Transmission à la plateforme du client',
  'fr:201': 'Réception par la plateforme du client',
  'fr:202': 'Mise à disposition du client',
}

type StatusSummary = { delivery_status: Invoice['delivery_status']; status_code: string | null; events_count: number }

export function isInvoiceInFlight(invoice: Invoice) {
  if (invoice.cancelled_at) return false
  return invoice.state === 'sending' || (invoice.state === 'sent' && IN_FLIGHT_STATUSES.includes(invoice.status_code))
}

// Follows an invoice that was just sent: asks the platform for its new statuses every few seconds and reloads the
// page when one arrived. Returns the step being waited for, or null once the invoice rests.
export function useInvoiceLiveStatus(invoice: Invoice) {
  const { current_organization } = usePage<SharedProps>().props
  const inFlight = isInvoiceInFlight(invoice)
  const eventsCount = invoice.events?.length ?? 0

  useEffect(() => {
    if (!inFlight) return

    const url = `/organizations/${current_organization?.slug}/invoices/${invoice.id}/status_refresh`
    const stopAt = Date.now() + MAX_FOLLOW_DURATION
    let timeout: ReturnType<typeof setTimeout>
    let stopped = false

    const changed = (summary: StatusSummary) =>
      summary.delivery_status !== invoice.delivery_status ||
      summary.status_code !== invoice.status_code ||
      summary.events_count !== eventsCount

    const poll = async () => {
      try {
        const { data: summary } = await axios.post<StatusSummary>(url)
        if (stopped) return
        if (changed(summary)) {
          router.reload({ only: ['invoice', 'vat_breakdown'], showProgress: false })
          return
        }
      } catch {
        // A missed poll is retried on the next tick.
      }
      if (!stopped && Date.now() < stopAt) timeout = setTimeout(poll, POLL_INTERVAL)
    }

    timeout = setTimeout(poll, POLL_INTERVAL)
    return () => {
      stopped = true
      clearTimeout(timeout)
    }
  }, [inFlight, invoice.id, invoice.delivery_status, invoice.status_code, eventsCount, current_organization?.slug])

  if (!inFlight) return null
  return NEXT_STEP_LABELS[invoice.status_code ?? 'none'] ?? null
}
